/**
 * ProtectionTierModal (Spec 201) — edit a protection tier's recovery objective (RPO + RTO + note).
 *
 * The five tiers are a fixed ladder (Starter → Platinum); this edits what each one MEANS. RPO/RTO
 * are entered as a value + unit (minutes/hours/days/weeks), stored canonically in HOURS, and shown
 * back in the unit that yields the lowest clean whole number (e.g. 720h ⇄ 30 days, 168h ⇄ 1 week).
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { Modal, Button, Input, Select, Textarea } from '@/components/ui';
import { tiersApi, ProtectionTier } from '../../api/tiers';
import { ProtectionTierBadge } from '../common/ProtectionTierBadge';

type UnitKey = 'minutes' | 'hours' | 'days' | 'weeks';
// Largest → smallest, so the first clean (whole-number) match is the lowest-number representation.
const UNITS: { key: UnitKey; label: string; hours: number }[] = [
  { key: 'weeks', label: 'Weeks', hours: 168 },
  { key: 'days', label: 'Days', hours: 24 },
  { key: 'hours', label: 'Hours', hours: 1 },
  { key: 'minutes', label: 'Minutes', hours: 1 / 60 },
];
const UNIT_OPTIONS = [...UNITS].reverse().map((u) => ({ value: u.key, label: u.label }));

/** Pick the unit that shows `hours` as the lowest clean whole number (else hours with decimals). */
const fromHours = (hours: number | null): { value: string; unit: UnitKey } => {
  if (hours == null) return { value: '', unit: 'hours' };
  if (hours === 0) return { value: '0', unit: 'hours' };
  for (const u of UNITS) {
    const v = hours / u.hours;
    if (Math.abs(v - Math.round(v)) < 1e-6 && Math.round(v) >= 1) return { value: String(Math.round(v)), unit: u.key };
  }
  return { value: String(hours), unit: 'hours' };
};

const toHours = (value: string, unit: UnitKey): number | null => {
  if (value.trim() === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n * (UNITS.find((u) => u.key === unit)?.hours ?? 1);
};

interface Props {
  open: boolean;
  tier: ProtectionTier | null;
  onClose: () => void;
}

export const ProtectionTierModal: React.FC<Props> = ({ open, tier, onClose }) => {
  const qc = useQueryClient();
  const [rpoValue, setRpoValue] = useState('');
  const [rpoUnit, setRpoUnit] = useState<UnitKey>('hours');
  const [rtoValue, setRtoValue] = useState('');
  const [rtoUnit, setRtoUnit] = useState<UnitKey>('hours');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !tier) return;
    setError(null);
    const rpo = fromHours(tier.rpoHours);
    const rto = fromHours(tier.rtoHours);
    setRpoValue(rpo.value);
    setRpoUnit(rpo.unit);
    setRtoValue(rto.value);
    setRtoUnit(rto.unit);
    setDescription(tier.description ?? '');
  }, [open, tier]);

  const save = async () => {
    if (!tier) return;
    setSaving(true);
    setError(null);
    try {
      await tiersApi.update(tier.tierName, {
        rpoHours: toHours(rpoValue, rpoUnit),
        rtoHours: toHours(rtoValue, rtoUnit),
        description: description.trim() === '' ? null : description.trim(),
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['backup', 'tiers'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'dash', 'readiness'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] }),
      ]);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the tier.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Edit protection tier"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ProtectionTierBadge tier={tier?.tierName} size="md" />
        </div>

        <div className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
          <p>
            <strong>RPO</strong> (Recovery Point Objective) = the maximum acceptable age of the latest backup — how much
            data loss is tolerable. <strong>RTO</strong> (Recovery Time Objective) = how quickly you must be able to restore.
            Pick any unit; values are stored in hours. The dashboard flags an entity at-risk when its last successful backup
            is older than this tier&apos;s RPO.
          </p>
        </div>

        {error && <p className="rounded-md border border-[var(--color-status-danger)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-status-danger)]">{error}</p>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">RPO (max age of latest backup)</label>
            <div className="flex gap-2">
              <Input type="number" min="0" step="1" value={rpoValue} onChange={(e) => setRpoValue(e.target.value)} className="w-24" />
              <Select value={rpoUnit} onChange={(e) => setRpoUnit(e.target.value as UnitKey)} options={UNIT_OPTIONS} />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">RTO (target time to restore)</label>
            <div className="flex gap-2">
              <Input type="number" min="0" step="1" value={rtoValue} onChange={(e) => setRtoValue(e.target.value)} className="w-24" />
              <Select value={rtoUnit} onChange={(e) => setRtoUnit(e.target.value as UnitKey)} options={UNIT_OPTIONS} />
            </div>
          </div>
        </div>

        <Textarea label="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="e.g. Daily recovery point" />
      </div>
    </Modal>
  );
};

export default ProtectionTierModal;
